import { useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'

import ErrorIcon from '@mui/icons-material/ErrorOutline'
import HistoryIcon from '@mui/icons-material/History'
import ViewModuleIcon from '@mui/icons-material/ViewModule'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { useTheme } from '@mui/material/styles'
import { type ColDef, type IDatasource } from 'ag-grid-community'
import { AgGridReact, type CustomCellRendererProps } from 'ag-grid-react'
import { z } from 'zod'

import { api } from '~/api'
import { useUser } from '~/common/hooks'
import { BaseStockItem } from '~/common/schemas/stock'
import { useAppSelector } from '~/store'
import BundleItem from './bundle-item'
import AdjustmentHistory from './stock-record-adjustment-history'

import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-quartz.css'

const StockRecord = z.object({
    id: z.number(),
    quantity: z.number(),
    barcode: z.string().nullable(),
    location: z.object({
        id: z.number(),
    }),
    stock_item: z.object({
        id: z.number(),
        sku: z.string().nullish(),
    }),
})
type StockRecord = z.infer<typeof StockRecord>

const StockRecordSimple = z.object({
    id: z.number(),
    quantity: z.number(),
    location: z.number(),
})
type StockRecordSimple = z.infer<typeof StockRecordSimple>

const StockItemState = BaseStockItem.pick({
    barcode: true,
    buffer_threshold: true,
    id: true,
    is_bundle: true,
    sku: true,
}).extend({
    name: z.string(),
    reserved_stock: z.number(),
    is_bundle_member: z.boolean(),
    total_quantity: z.number(),
    stock_records: z.array(StockRecordSimple),
})
type StockItemState = z.infer<typeof StockItemState>

const FETCH_LIMIT = 25

function validateStockRecordQuantities(
    reservedStock: number,
    record: Pick<StockRecord, 'quantity'>[]
): boolean {
    const totalQuantity = record.reduce((acc, record) => acc + record.quantity, 0)

    return totalQuantity >= reservedStock
}

type CellRenderer<T extends keyof StockItemState> = CustomCellRendererProps<
    StockItemState,
    StockItemState[T]
>

type EditableKeys = 'buffer_threshold' | 'barcode' | 'name'
type ValueTypeMap<K extends EditableKeys> = K extends 'buffer_threshold' ? number : string

export type Props = {
    onQuantityChange?: (stockRecord: {
        id: number
        quantity: number
        location: number
        stockItem: { id: number; sku: string }
    }) => void
    onStockItemChange?: <Key extends EditableKeys>(params: {
        stockItemId: number
        key: Key
        value: ValueTypeMap<Key>
    }) => void
}

export default function StockManagerTable({ onQuantityChange, onStockItemChange }: Props) {
    const ownerId = useAppSelector((state) => state.initial.accountOwner.id)
    const user = useUser()
    const { data } = api.users.locations.useQuery(['users', 'locations'], {
        params: {
            id: user.id,
        },
    })

    const userLocations = useMemo(() => {
        return data?.body ?? []
    }, [data?.body])

    const [stockItems, setStockItems] = useState<StockItemState[]>([])
    const [selectedStockItemId, setSelectedStockItemId] = useState<number | null>(null)

    const selectedStockItem = useMemo(() => {
        return stockItems.find((item) => item.id === selectedStockItemId)
    }, [stockItems, selectedStockItemId])

    const [selectedStockRecordId, setSelectedStockRecordId] = useState<number | null>(null)

    const selectedStockRecord = useMemo(() => {
        return stockItems
            .flatMap((item) => item.stock_records)
            .find((record) => record.id === selectedStockRecordId)
    }, [stockItems, selectedStockRecordId])

    const location = useLocation()
    const theme = useTheme()

    const datasource: IDatasource = useMemo(() => {
        return {
            async getRows({ successCallback, startRow, endRow }) {
                const searchParams = new URLSearchParams(location.search)

                const data = await api.users.stockItems.query({
                    params: {
                        accountOwnerId: ownerId,
                    },
                    query: {
                        offset: startRow,
                        limit: endRow - startRow,
                        search: searchParams.get('search') ?? undefined,
                        is_bundle: searchParams.get('is_bundle') === 'True',
                        show_reserved_stock_issues:
                            searchParams.get('show_reserved_stock_issues') === 'True',
                    },
                })

                setStockItems((prev) => [...prev, ...data.body.results])

                successCallback(data.body.results, data.body.count)
            },
        }
    }, [location.search, ownerId])

    const columnDefs: ColDef<StockItemState>[] = useMemo(() => {
        const locationCellStyle = ({ data }: { data?: StockItemState }) => {
            if (!data) {
                return undefined
            }

            const isValid = validateStockRecordQuantities(data.reserved_stock, data.stock_records)

            return {
                backgroundColor: isValid ? 'initial' : theme.palette.error.light,
            }
        }

        return [
            {
                field: 'sku',
                headerName: 'SKU',
                pinned: 'left',
                cellStyle: {
                    backgroundColor: 'var(--ag-header-background-color)',
                },
                cellRenderer: ({ value, data }: CellRenderer<'sku'>) => {
                    if (!value) {
                        return <Skeleton />
                    }

                    return (
                        <Stack
                            direction="row"
                            justifyContent="space-between"
                            sx={{
                                '& > button': {
                                    transition: 'opacity 100ms ease-in-out',
                                    opacity: 0,
                                },
                                '&:hover button': {
                                    opacity: 1,
                                },
                            }}
                        >
                            {value}
                            <Tooltip title={gettext('View bundle')}>
                                <IconButton
                                    disabled={data?.is_bundle_member}
                                    size="small"
                                    onClick={() => {
                                        if (data?.id) {
                                            setSelectedStockItemId(data.id)
                                        }
                                    }}
                                >
                                    <ViewModuleIcon />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    )
                },
            },
            {
                field: 'barcode',
                editable: true,
                headerName: gettext('Barcode'),
                cellRenderer: ({ value }: CellRenderer<'barcode'>) => {
                    if (typeof value === 'undefined') {
                        return <Skeleton />
                    }

                    return value
                },
                onCellValueChanged: ({ data, newValue }) => {
                    onStockItemChange?.({
                        stockItemId: data.id,
                        key: 'barcode',
                        value: newValue,
                    })
                },
            },
            {
                field: 'name',
                editable: true,
                headerName: gettext('Name'),
                cellRenderer: ({ value }: CellRenderer<'name'>) => {
                    if (!value) {
                        return <Skeleton />
                    }

                    return value
                },
                onCellValueChanged: ({ data, newValue }) => {
                    onStockItemChange?.({
                        stockItemId: data.id,
                        key: 'name',
                        value: newValue,
                    })
                },
            },
            {
                headerName: 'Fullfilment locations',
                children: [
                    ...(userLocations.map((location) => ({
                        editable: true,
                        headerName: location.name,
                        cellStyle: locationCellStyle,
                        cellEditor: 'agNumberCellEditor',
                        cellEditorParams: {
                            min: 0,
                        },
                        cellRenderer: ({ value }: { value: StockRecordSimple }) => {
                            if (typeof value === 'undefined') {
                                return <Skeleton />
                            }

                            return (
                                <Stack
                                    direction="row"
                                    justifyContent="space-between"
                                    sx={{
                                        '& > button': {
                                            transition: 'opacity 100ms ease-in-out',
                                            opacity: 0,
                                        },
                                        '&:hover button': {
                                            opacity: 1,
                                        },
                                    }}
                                >
                                    {value?.quantity}
                                    <Tooltip
                                        title={gettext('View adjustment history')}
                                        placement="bottom-start"
                                    >
                                        <IconButton
                                            size="small"
                                            onClick={() => {
                                                setSelectedStockRecordId(value?.id)
                                            }}
                                        >
                                            <HistoryIcon />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                            )
                        },
                        valueGetter: ({ data }) => {
                            return data?.stock_records.find(
                                (record) => record.location === location.id
                            )
                        },
                        valueSetter: ({ data, newValue }) => {
                            if (isNaN(newValue)) {
                                return false
                            }

                            const updatedStockRecords = data.stock_records.map((record) => {
                                if (record.location === location.id) {
                                    return {
                                        ...record,
                                        quantity: newValue,
                                    }
                                }

                                return record
                            })

                            data.stock_records = updatedStockRecords

                            return true
                        },
                        onCellValueChanged: ({ data, newValue }) => {
                            const isValid = validateStockRecordQuantities(
                                data.reserved_stock,
                                data.stock_records
                            )
                            if (!isValid) {
                                return
                            }

                            onQuantityChange?.({
                                ...newValue,
                                id: newValue.location,
                                stockItem: { id: data.id, sku: data.sku },
                            })
                        },
                    })) satisfies ColDef<StockItemState>[]),
                    {
                        headerName: 'Total',
                        cellStyle: locationCellStyle,
                        cellRenderer: ({
                            data,
                        }: CustomCellRendererProps<StockItemState, StockItemState>) => {
                            if (!data) {
                                return <Skeleton />
                            }

                            const isValid = validateStockRecordQuantities(
                                data.reserved_stock,
                                data.stock_records
                            )

                            const reservedStock = data.reserved_stock

                            return (
                                <Stack
                                    width="100%"
                                    direction="row"
                                    justifyContent="space-between"
                                    alignItems="center"
                                >
                                    <Box>
                                        {data.stock_records.reduce(
                                            (acc, record) => acc + record.quantity,
                                            0
                                        )}
                                    </Box>
                                    {data.reserved_stock > 0 && (
                                        <Stack direction="row" alignItems="center" gap={1}>
                                            {!isValid && (
                                                <Tooltip
                                                    title={`You have ${reservedStock} units reserved in channel campaigns. Adjust stock so the total is at least ${reservedStock}.`}
                                                >
                                                    <ErrorIcon
                                                        color="error"
                                                        sx={{ fontSize: 28 }}
                                                    />
                                                </Tooltip>
                                            )}
                                            <Tooltip title={'Total reserved stock'}>
                                                <Box
                                                    sx={{
                                                        cursor: 'help',
                                                        width: 24,
                                                        height: 24,
                                                        color: theme.palette.grey[600],
                                                        borderRadius: 9999,
                                                        border: '2px solid',
                                                        textAlign: 'center',
                                                    }}
                                                >
                                                    <Typography variant="subtitle2">R</Typography>
                                                </Box>
                                            </Tooltip>
                                            <Typography fontWeight={400} variant="subtitle2">
                                                {data.reserved_stock}
                                            </Typography>
                                        </Stack>
                                    )}
                                </Stack>
                            )
                        },
                    } satisfies ColDef<StockItemState, number>,
                ],
            },
            {
                field: 'buffer_threshold',
                editable: true,
                headerName: gettext('Buffer Threshold'),
                headerTooltip: gettext(
                    'When total system stock for a sku is below the threshold, the stock for the sku will be distributed across channels'
                ),
                cellEditor: 'agNumberCellEditor',
                cellEditorParams: {
                    min: 0,
                },
                cellRenderer: ({ value }: CellRenderer<'buffer_threshold'>) => {
                    if (typeof value === 'undefined') {
                        return <Skeleton />
                    }

                    return value
                },
                onCellValueChanged: ({ data, newValue }) => {
                    onStockItemChange?.({
                        stockItemId: data.id,
                        key: 'buffer_threshold',
                        value: newValue,
                    })
                },
            },
        ]
    }, [
        theme.palette.error,
        theme.palette.grey[600],
        userLocations,
        onQuantityChange,
        onStockItemChange,
    ])

    return (
        <>
            <Box
                className="ag-theme-quartz"
                sx={{
                    '--ag-font-family': 'Roboto',
                    height: '100%',
                }}
            >
                <AgGridReact<StockItemState>
                    rowModelType="infinite"
                    cacheBlockSize={FETCH_LIMIT}
                    infiniteInitialRowCount={10}
                    datasource={datasource}
                    columnDefs={columnDefs}
                    gridOptions={{
                        autoSizeStrategy: {
                            type: 'fitGridWidth',
                            columnLimits: [{ colId: 'barcode', maxWidth: 200 }],
                        },
                        defaultColDef: {
                            sortable: false,
                        },
                        stopEditingWhenCellsLoseFocus: true,
                    }}
                    tooltipShowDelay={0}
                />
            </Box>

            {!!selectedStockRecord && (
                <AdjustmentHistory
                    stockRecord={selectedStockRecord}
                    open={!!selectedStockRecord}
                    onClose={() => setSelectedStockRecordId(null)}
                />
            )}

            {!!selectedStockItem && (
                <BundleItem
                    stockItem={selectedStockItem}
                    open={!!selectedStockItem}
                    onClose={() => setSelectedStockItemId(null)}
                />
            )}
        </>
    )
}
